"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Phone, CalendarCheck, ShieldCheck, Ruler } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
export default function Estimate() {
  const shouldReduceMotion = useReducedMotion();
  const trustHighlights = [{
    icon: CalendarCheck,
    text: "Detailed timeline & planning"
  }, {
    icon: Ruler,
    text: "Custom layout & cabinetry design"
  }, {
    icon: ShieldCheck,
    text: "Clear project scope"
  }];
  return <section id="start-your-kitchen-project" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 overflow-hidden border-t border-border">
      {/* Subtle architectural background grid accents */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_top_right,var(--color-secondary)_0%,transparent_60%)]" />

      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }}>
          <Card className="relative overflow-hidden rounded-2xl border border-border bg-card/90 backdrop-blur-md shadow-xl p-0 transition-[border-color,box-shadow] duration-300 hover:border-border hover:shadow-2xl">
            {/* Architectural copper hairline accent */}
            <div aria-hidden="true" className="h-1 w-full bg-gradient-to-r from-primary via-primary/80 to-transparent" />

            <CardContent className="px-6 py-10 sm:px-12 sm:py-14 lg:p-16 text-center flex flex-col items-center">
              <Badge variant="secondary" className="mb-6 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-secondary-foreground border border-border">
                Decatur custom remodeling
              </Badge>

              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground max-w-2xl leading-tight">
                Ready to design your dream kitchen?
              </h2>

              <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
                Share your ideas with our Decatur design team, review custom material choices, and receive a clear, transparent project estimate tailored to your home.
              </p>

              {/* Action buttons & direct call */}
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto">
                <Button asChild size="lg" className="w-full sm:w-auto h-12 px-8 rounded-lg bg-primary text-primary-foreground font-semibold shadow-md hover:bg-primary/90 transition-[background-color,transform,box-shadow] active:scale-[0.98]">
                  <Link to="#" className="inline-flex items-center justify-center gap-2">
                    <span>Request an estimate</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto h-12 px-6 rounded-lg border-border bg-background text-foreground hover:bg-secondary font-medium transition-colors">
                  <a href="tel:+14708479076" className="inline-flex items-center justify-center gap-2">
                    <Phone className="size-4 text-muted-foreground" />
                    <span>(470) 847-9076</span>
                  </a>
                </Button>
              </div>

              {/* Supporting proof points */}
              <div className="mt-12 pt-8 border-t border-border w-full grid grid-cols-1 sm:grid-cols-3 gap-4 text-left sm:text-center">
                {trustHighlights.map((item, index) => {
                const Icon = item.icon;
                return <div key={item.text} data-index={index} className="flex items-center sm:justify-center gap-2.5 text-sm text-muted-foreground">
                      <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-secondary text-foreground">
                        <Icon className="size-4" />
                      </span>
                      <span>{item.text}</span>
                    </div>;
              })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}